import { createContext, useContext, useEffect, useRef, useState } from "react";
import { api, setAuthToken, setRefreshToken, apiError } from "@/lib/api";
import { clearActiveJob } from "@/lib/activeJob";
import { getAssertion, finishLoginPasskey } from "@/lib/webauthn";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

// Server-enforced idle policy is authoritative; these mirror it for UX.
const IDLE_MS = 15 * 60 * 1000;           // 15 min of genuine inactivity
const HEARTBEAT_THROTTLE_MS = 60 * 1000;  // advance last_active at most once/min

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=anon, obj=auth
  const [nav, setNav] = useState([]);
  const lastActivityRef = useRef(Date.now());
  const lastBeatRef = useRef(0);

  const loadNav = async () => {
    try {
      const { data } = await api.get("/nav");
      setNav(data.modules);
    } catch { setNav([]); }
  };

  useEffect(() => {
    // Public auth pages (reset/forgot/login) must work for logged-out users. Do NOT run the
    // anonymous /auth/me session check here — it 401s by design and could bounce the reset
    // page to /login. Render as anonymous and let those pages manage their own flow.
    const PUBLIC_PREFIXES = ["/login", "/forgot-password", "/reset-password"];
    if (PUBLIC_PREFIXES.some((p) => window.location.pathname.startsWith(p))) {
      setUser(false);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        await loadNav();
      } catch {
        setUser(false);
      }
    })();
  }, []);

  const completeSession = async (data) => {
    if (data.token) setAuthToken(data.token);
    if (data.refresh_token) setRefreshToken(data.refresh_token);
    setUser(data);
    await loadNav();
    return data;
  };

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.mfa_required) return data; // caller shows the 2FA challenge step
    return completeSession(data);
  };

  const verifyMfa = async (mfa_token, code, recovery = false) => {
    const { data } = await api.post(recovery ? "/auth/mfa/recovery-login" : "/auth/mfa/verify", { mfa_token, code });
    return completeSession(data);
  };

  // Options are pre-fetched by the caller (Login page) BEFORE the tap; here we
  // invoke get() synchronously to preserve iOS Safari user activation.
  const passkeyLogin = async (options) => {
    const assertion = await getAssertion(options.publicKey);
    const data = await finishLoginPasskey(options.tx, assertion);
    return completeSession(data);
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setAuthToken(null);
    setRefreshToken(null);
    clearActiveJob();          // wipe this user's active-job UI context
    setUser(false);
    setNav([]);
  };

  // Local-only teardown (no server call) — used when another tab already
  // revoked the session, to avoid a redundant/failed logout request.
  const clearLocalSession = () => {
    setAuthToken(null);
    setRefreshToken(null);
    clearActiveJob();
    setUser(false);
    setNav([]);
  };

  // Session lifecycle: heartbeat on genuine activity (visible only), a hard
  // idle timer, re-auth on return-to-foreground after timeout, and multi-tab
  // logout. Background/hidden traffic never resets the timer (server READs but
  // does not advance last_active; heartbeat only fires on real activity).
  useEffect(() => {
    if (!user || user === false) return;
    lastActivityRef.current = Date.now();

    const markActivity = () => {
      if (document.visibilityState !== "visible") return;
      lastActivityRef.current = Date.now();
      const now = Date.now();
      if (now - lastBeatRef.current >= HEARTBEAT_THROTTLE_MS) {
        lastBeatRef.current = now;
        api.post("/auth/session/heartbeat").catch(() => {});
      }
    };

    const events = ["pointerdown", "keydown", "click", "scroll", "touchstart", "mousemove"];
    events.forEach((e) => window.addEventListener(e, markActivity, { passive: true }));

    const idleCheck = setInterval(() => {
      if (Date.now() - lastActivityRef.current >= IDLE_MS) logout();
    }, 15000);

    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      // Returned after 15+ min idle (e.g. backgrounded/locked): force re-auth
      // immediately so no protected content is shown before authentication.
      if (Date.now() - lastActivityRef.current >= IDLE_MS) { logout(); return; }
      // Otherwise confirm the session is still valid server-side.
      api.get("/auth/me").catch(() => {});
    };
    document.addEventListener("visibilitychange", onVisible);

    const onStorage = (e) => {
      if (e.key === "sg_token" && !e.newValue) clearLocalSession();
    };
    window.addEventListener("storage", onStorage);

    return () => {
      events.forEach((e) => window.removeEventListener(e, markActivity));
      clearInterval(idleCheck);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("storage", onStorage);
    };
  }, [user]);

  // bfcache (Safari/iOS) hardening: a full-document Back/Forward can restore a
  // cached authenticated page WITHOUT re-running JS, briefly exposing protected
  // content after the session expired/was revoked. On a bfcache restore only
  // (event.persisted), re-validate the session BEFORE showing protected content.
  // Normal in-app SPA Back/Forward does not fire pageshow, so a still-valid
  // session is unaffected beyond a brief re-check.
  useEffect(() => {
    const onPageShow = (e) => {
      if (!e.persisted) return; // only handle bfcache restores
      const PUBLIC = ["/login", "/forgot-password", "/reset-password"];
      if (PUBLIC.some((p) => window.location.pathname.startsWith(p))) return;
      if (Date.now() - lastActivityRef.current >= IDLE_MS) { clearLocalSession(); return; }
      setUser(null); // blank protected content while we re-validate
      (async () => {
        try { const { data } = await api.get("/auth/me"); setUser(data); await loadNav(); }
        catch { clearLocalSession(); }
      })();
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  return (
    <AuthCtx.Provider value={{ user, nav, login, verifyMfa, passkeyLogin, logout, apiError }}>
      {children}
    </AuthCtx.Provider>
  );
}
